import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { useHapticFeedback } from '@blade/ui-kit'
import {
  openBuyBoostersFlowEvent,
  openBuyPremiumFlowEvent,
} from '@root/entities/premium'
import {
  CheckCurrencyActive,
  CheckCurrencyFailed,
  CheckCurrencyPassed,
  type CheckCurrencyReatom,
} from '@root/features/premium/check-currency'
import { useOptionalAtom } from '@root/shared/lib'
import { useTranslation } from '@root/shared/localization'
import { Drawer } from '@root/shared/ui-kit'

import { PremiumScreenActions } from './premium-screen-actions'

type PremiumScreenResultDrawerProps = {
  model: CheckCurrencyReatom
}

export const PremiumScreenResultDrawer: React.FC<
  PremiumScreenResultDrawerProps
> = ({ model }) => {
  const { t } = useTranslation()

  const [state] = useAtom(model.checkCurrencyStateAtom)
  const resetCheckState = useAction(model.resetCheckStateAction)
  const premium = useOptionalAtom(state.value?.premiumInfo?.premium)
  const haptics = useHapticFeedback()
  const openBuyPremiumDrawer = useAction(openBuyPremiumFlowEvent)
  const openBuyBoostersDrawer = useAction(openBuyBoostersFlowEvent)

  const open = AsyncStateN.isSuccess(state)

  const handleClose = () => resetCheckState()

  // TODO: fix glitching skeleton for CheckCurrencyFailed on mobiles
  const renderContent = () => {
    if (premium && state.value) {
      return <CheckCurrencyActive result={state.value} />
    }
    if (state.value && state.value.hasPassedSecurityChecks) {
      return <CheckCurrencyPassed result={state.value} />
    }
    return <CheckCurrencyFailed result={state.value} model={model} />
  }

  return (
    <Root
      open={open}
      onOpenChange={handleClose}
      title={t('premium.check.result')}
    >
      <CheckResult>{renderContent()}</CheckResult>

      {state.value && state.value.hasPassedSecurityChecks ? (
        <PremiumScreenActions
          onBuyBoosters={() => {
            if (state.value.premiumInfo) {
              haptics.impactOccurred('light')
              openBuyBoostersDrawer({
                currency: state.value.currency,
                premium: state.value.premiumInfo,
              })
            }
          }}
          onBuySubscription={() => {
            haptics.impactOccurred('light')
            openBuyPremiumDrawer({
              currency: state.value.currency,
              premium: state.value.premiumInfo,
            })
          }}
          result={state.value}
        />
      ) : undefined}
    </Root>
  )
}

const Root = styled(Drawer)`
  background-color: var(--background-1);
  height: var(--max-drawer-height);
`

const CheckResult = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;

  text-align: center;
  font-style: normal;
  align-items: center;
  height: min-content;

  padding-block: 16px 32px;
`
